// src/components/common/ProfileModal.tsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  UserCircleIcon, 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon,
  EnvelopeIcon,
  UserIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import authService from '../../features/auth/services/authService';
import { UserInfoResponse, UserProfileUpdateRequest } from '../../features/auth/types';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const [userInfo, setUserInfo] = useState<UserInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'overview'>('overview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchUserInfo();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const response = await authService.getUserInfo();
      setUserInfo(response);
      setFormData({
        first_name: response.user.first_name || '',
        last_name: response.user.last_name || '',
        email: response.user.email || ''
      });
    } catch (err: any) {
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate form data
      if (!formData.first_name || !formData.last_name || !formData.email) {
        setError('Please fill in all required fields');
        return;
      }

      // Call the real API to update user profile
      const updateData: UserProfileUpdateRequest = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email
      };

      const response = await authService.updateProfile(updateData);
      
      setSuccess(response.message || 'Profile updated successfully!');
      setEditing(false);
      
      // Update local user state with the response from server
      setUserInfo({
        user: response.user,
        group: response.group
      });
      
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(err?.message || err?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (userInfo) {
      setFormData({
        first_name: userInfo.user.first_name || '',
        last_name: userInfo.user.last_name || '',
        email: userInfo.user.email || ''
      });
    }
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-[95vw] h-[85vh] max-w-6xl overflow-hidden border border-theme-border dark:border-dark-theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col md:flex-row h-full">
          {/* Sidebar */}
          <div className="w-full md:w-64 bg-theme-bg dark:bg-dark-theme-bg md:rounded-l-lg p-4 md:p-6 flex-shrink-0 border-b md:border-b-0 md:border-r border-theme-border dark:border-dark-theme-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                Profile
              </h2>
              <button
                onClick={onClose}
                className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
                aria-label="Close profile settings"
                title="Close profile settings modal"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <nav className="space-y-2">
              {[
                { id: 'overview', label: 'Overview', icon: UserCircleIcon }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSection(tab.id as any)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors ${
                      activeSection === tab.id
                        ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                        : 'text-theme-text-secondary dark:text-dark-theme-text-secondary hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium text-sm md:text-base">{tab.label}</span>
                  </button>
                );
              })}
            </nav>

          </div>

          {/* Main Content */}
          <div className="flex-1 p-4 md:p-6 overflow-y-auto">
            {loading ? (
              <div className="animate-pulse">
                <div className="h-6 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded w-1/4 mb-6"></div>
                <div className="space-y-4">
                  <div className="h-20 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded"></div>
                  <div className="h-16 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded"></div>
                  <div className="h-16 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded"></div>
                </div>
              </div>
            ) : (
              <>
                {/* Success/Error Messages */}
                {success && (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                    <div className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                      <p className="text-green-700 dark:text-green-300">{success}</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <div className="flex items-center">
                      <XMarkIcon className="h-5 w-5 text-red-500 mr-2" />
                      <p className="text-red-700 dark:text-red-300">{error}</p>
                    </div>
                  </div>
                )}

                {/* Profile Overview Section */}
                {activeSection === 'overview' && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
                      Profile Overview
                    </h3>

                    <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-4 md:p-6 border border-theme-border dark:border-dark-theme-border">
                      <div className="grid grid-cols-1 gap-4 md:gap-6">
                        {/* First Name */}
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                            First Name
                          </label>
                          <div className="relative">
                            <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
                            <input
                              type="text"
                              value={formData.first_name}
                              onChange={(e) => handleInputChange('first_name', e.target.value)}
                              disabled={!editing}
                              className="w-full pl-10 pr-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                              placeholder="Enter your first name"
                            />
                          </div>
                        </div>

                        {/* Last Name */}
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                            Last Name
                          </label>
                          <div className="relative">
                            <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
                            <input
                              type="text"
                              value={formData.last_name}
                              onChange={(e) => handleInputChange('last_name', e.target.value)}
                              disabled={!editing}
                              className="w-full pl-10 pr-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                              placeholder="Enter your last name"
                            />
                          </div>
                        </div>

                        {/* Email */}
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                            Email Address
                          </label>
                          <div className="relative">
                            <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
                            <input
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleInputChange('email', e.target.value)}
                              disabled={!editing}
                              className="w-full pl-10 pr-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                              placeholder="Enter your email address"
                            />
                          </div>
                        </div>

                        {/* Company Name (Read-only) - HIDDEN */}
                        {false && userInfo?.user?.group_details?.name && (
                          <div>
                            <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                              Company Name
                            </label>
                            <div className="relative">
                              <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
                              <input
                                type="text"
                                value={userInfo.user.group_details.name}
                                disabled
                                className="w-full pl-10 pr-4 py-2 border border-theme-border dark:border-dark-theme-border rounded-md bg-neutral-100 dark:bg-neutral-800 text-theme-text-secondary dark:text-dark-theme-text-secondary cursor-not-allowed"
                              />
                            </div>
                            <p className="mt-1 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary">
                              Company membership is managed by your administrator
                            </p>
                          </div>
                        )}

                        {/* Account Information Section */}
                        <div className="border-t border-theme-border dark:border-dark-theme-border pt-6 mt-6">
                          <h4 className="text-lg font-medium text-theme-text dark:text-dark-theme-text mb-4">
                            Account Information
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Member Since */}
                            <div className="border border-theme-border/30 dark:border-dark-theme-border/30 bg-transparent rounded-lg p-4">
                              <div className="flex items-center space-x-3 mb-2">
                                <UserCircleIcon className="h-5 w-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                                <h5 className="font-medium text-theme-text dark:text-dark-theme-text">Member Since</h5>
                              </div>
                              <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                                {userInfo?.user?.date_joined ? new Date(userInfo.user.date_joined).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>

                            {/* Staff Status */}
                            <div className="border border-theme-border/30 dark:border-dark-theme-border/30 bg-transparent rounded-lg p-4">
                              <div className="flex items-center space-x-3 mb-2">
                                <UserCircleIcon className="h-5 w-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                                <h5 className="font-medium text-theme-text dark:text-dark-theme-text">Staff Status</h5>
                              </div>
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                userInfo?.user?.is_staff 
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                  : 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
                              }`}>
                                {userInfo?.user?.is_staff ? 'Staff Member' : 'Regular User'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProfileModal;